import { z } from "zod";

import { simpleDateSchema } from "@/src/models/common-schema";
import { IdentificationCardDocumentType } from "@/src/models/constants/document-types";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { patientMinimalSchema } from "@/src/models/patient";

export const identificationCardTypeDisplay: Record<IdentificationCardDocumentType, string> = {
  [IdentificationCardDocumentType.IDENTIFICATION_CARD]: "Identification Card",
};

const baseIdentificationCardFields = {
  patient: patientMinimalSchema,
  identificationNumber: z.string().nullable(),
  identificationType: z.string().nullable(),
  identificationIssuedBy: z.string().nullable(),
  identificationIssuedDate: simpleDateSchema.nullable(),
  identificationExpirationDate: simpleDateSchema.nullable(),
  dateReceived: DocumentDateSchema.shape.dateReceived,
};

const extendedIdentificationCardSchema = z.object({
  ...DocumentDateSchema.shape,
});

const identificationCardCustomFields: Record<IdentificationCardDocumentType, Record<string, z.ZodTypeAny>> = {
  [IdentificationCardDocumentType.IDENTIFICATION_CARD]: extendedIdentificationCardSchema.pick({ dateReceived: true })
    .shape,
};

const makeIdentificationCardSchema = (
  type: IdentificationCardDocumentType,
  customFields: Record<string, z.ZodTypeAny>
) =>
  z.object({
    type: z.literal(type),
    ...baseIdentificationCardFields,
    ...customFields,
  });

export const identificationCardSchemas: Record<
  IdentificationCardDocumentType,
  ReturnType<typeof makeIdentificationCardSchema>
> = (Object.values(IdentificationCardDocumentType) as IdentificationCardDocumentType[]).reduce(
  (acc, type) => {
    acc[type] = makeIdentificationCardSchema(type, identificationCardCustomFields[type]);

    return acc;
  },
  {} as Record<IdentificationCardDocumentType, ReturnType<typeof makeIdentificationCardSchema>>
);

const identificationCardSchemasArray = (
  Object.values(IdentificationCardDocumentType) as IdentificationCardDocumentType[]
).map((type) => identificationCardSchemas[type]) as [ReturnType<typeof makeIdentificationCardSchema>];

export const anyIdentificationCardSchema = z.discriminatedUnion("type", identificationCardSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type IdentificationCard = ExtractedDocumentFields<
  IdentificationCardDocumentType,
  typeof extendedIdentificationCardSchema,
  typeof baseIdentificationCardFields
> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const identificationCardShape: IdentificationCard = {
  id: "",
  type: IdentificationCardDocumentType.IDENTIFICATION_CARD,
  patient: null,
  identificationNumber: null,
  identificationType: null,
  identificationIssuedBy: null,
  identificationIssuedDate: null,
  identificationExpirationDate: null,
  dateReceived: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
